import * as React from 'react'

import { cn } from '../../utils/cn'
import { Tabs, TabsList, TabsTrigger } from './tabs'

export interface ToggleTabsItem {
  value: string
  label: React.ReactNode
  /** Leading icon element — auto-sized to 16px by the trigger. */
  icon?: React.ReactNode
  /** Render-gate for conditional tabs, so call sites stay declarative. */
  hidden?: boolean
}

interface ToggleTabsProps extends React.ComponentProps<typeof Tabs> {
  items: ToggleTabsItem[]
  listClassName?: string
  /** Extra classes per trigger — e.g. the sidebar's tighter padding and type scale. */
  triggerClassName?: string
}

// Matches the quotes page's sort tabs: a hairline container on the page ground with
// the active item picked out in brand tint, rather than a raised white pill.
// Scrolls rather than overflowing: the customer page carries twelve tabs, which runs
// off the column and under the action sidebar at anything short of a wide desktop.
// justify-start matters — the base list centres, and centred overflow spills out of
// the left edge where no amount of scrolling can reach it.
const LIST_CLASS =
  'border-ink-100 no-scrollbar h-auto max-w-full justify-start gap-0 overflow-x-auto rounded-xl border bg-transparent p-1'
const TRIGGER_CLASS =
  'data-[state=active]:bg-brand-100 data-[state=active]:text-ink shrink-0 gap-2 rounded-lg border-none data-[state=active]:shadow-none'

/**
 * The standard app toggle group: a hairline container with the active item in
 * brand tint. Tab content renders as children — either `<TabsContent>` blocks or
 * conditional rendering keyed on the controlled `value`.
 */
export function ToggleTabs({ items, listClassName, triggerClassName, children, ...props }: ToggleTabsProps) {
  const listRef = React.useRef<HTMLDivElement>(null)

  // Scrolls the selected tab into view rather than leaving the bar looking like nothing is
  // selected — these tabs are hash-driven, so a link can land on one that starts off-screen.
  // Own scrollLeft, not scrollIntoView: that one also moves the page under the tab bar.
  React.useEffect(() => {
    const list = listRef.current
    const active = list?.querySelector<HTMLElement>('[data-state="active"]')
    if (!list || !active) return
    if (active.offsetLeft < list.scrollLeft) {
      list.scrollLeft = active.offsetLeft
    } else if (active.offsetLeft + active.offsetWidth > list.scrollLeft + list.clientWidth) {
      list.scrollLeft = active.offsetLeft + active.offsetWidth - list.clientWidth
    }
  }, [props.value])

  return (
    <Tabs {...props}>
      <TabsList ref={listRef} className={cn(LIST_CLASS, listClassName)}>
        {items
          .filter((item) => !item.hidden)
          .map((item) => (
            <TabsTrigger key={item.value} value={item.value} className={cn(TRIGGER_CLASS, triggerClassName)}>
              {item.icon}
              {item.label}
            </TabsTrigger>
          ))}
      </TabsList>
      {children}
    </Tabs>
  )
}
